import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// Phase 1: Parallel fetching with selective fields
const fetchHomepageData = async () => {
  // Parallel fetch all data at once
  const [featuredResult, recentResult, recsResult] = await Promise.all([
    // Featured posts - fetch all featured articles
    supabase
      .from("blog_posts")
      .select(`
        id,
        title,
        slug,
        excerpt,
        cover_image,
        published_at,
        read_time,
        categories,
        tags,
        author:ai_authors(id, name, avatar_url, title)
      `)
      .eq("is_featured", true)
      .order("published_at", { ascending: false }),
    
    // Recent posts - exclude content field (can be 50KB+)
    supabase
      .from("blog_posts")
      .select(`
        id,
        title,
        slug,
        excerpt,
        cover_image,
        published_at,
        read_time,
        categories,
        tags,
        author:ai_authors(id, name, avatar_url, title)
      `)
      .eq("is_featured", false)
      .order("published_at", { ascending: false })
      .limit(3),
    
    // Recommendations
    supabase
      .from("recommendations")
      .select("id, title, description, image_url, url, category")
      .eq("section", "home-picks")
      .eq("is_active", true)
      .order("display_order", { ascending: true })
      .limit(3)
  ]);

  // Map database fields to component format
  type PostRow = NonNullable<typeof featuredResult.data>[number];
  const mapPost = (post: PostRow) => ({
    ...post,
    coverImage: post.cover_image,
    publishedAt: post.published_at,
    readTime: post.read_time,
    author: post.author ? {
      ...post.author,
      avatar: post.author.avatar_url
    } : null,
    categories: post.categories?.map((cat: string) => ({
      id: cat,
      name: cat
    })) || []
  });

  return {
    featuredPosts: featuredResult.data ? featuredResult.data.map(mapPost) : [],
    recentPosts: recentResult.data ? recentResult.data.map(mapPost) : [],
    recommendations: recsResult.data || []
  };
};

// Phase 2: React Query hook with intelligent caching
export const useHomepageData = () => {
  return useQuery({
    queryKey: ['homepage-data'],
    queryFn: fetchHomepageData,
    staleTime: 5 * 60 * 1000, // 5 minutes - static content cache
    gcTime: 10 * 60 * 1000, // 10 minutes garbage collection
  });
};
